import { IWealthicaSubscriptionDto } from './dtos'
import {
	IGetWealthicaTransactionsParams,
	IWealthicaInstitution,
	IWealthicaProvider,
	IWealthicaTransaction,
} from './models'

export const UNIFIED_ADDON_PREFIX = 'blocksentry_blocksentry_'

export default class WealthicaAPI {
	static addon: typeof window.Addon | undefined

	static async init() {
		return new Promise(resolve => {
			const script = document.createElement('script')
			script.src = 'https://wealthica.github.io/wealthica.js/dist/addon.min.js'
			script.onload = () => {
				try {
					this.addon = new Addon()

					this.addon.on('init', resolve)
				} catch (err) {
					resolve(undefined)
				}
			}
			document.head.appendChild(script)
		})
	}

	static async getTransactions(options: IGetWealthicaTransactionsParams): Promise<IWealthicaTransaction[]> {
		return await this.addon?.api.getTransactions(options)
	}

	static async getInstitutions(): Promise<IWealthicaInstitution[]> {
		return await this.addon?.api.getInstitutions()
	}

	static async getCryptoProviders(): Promise<IWealthicaProvider[]> {
		return await this.addon?.request({
			method: 'GET',
			endpoint: 'providers',
			query: {
				group: 'vezgo',
			},
		})
	}

	static async getPaginatedTransactions({
		last = '',
		vezgo,
		filteredInstitutionIdsString,
	}: {
		last?: string
		vezgo?: boolean
		filteredInstitutionIdsString: string
	}): Promise<IWealthicaTransaction[]> {
		const newTransactions = await this.getTransactions({
			from: '2004-01-01',
			institutions: filteredInstitutionIdsString,
			vezgo: vezgo || undefined,
			limit: 1000,
			last,
		})

		if (newTransactions.length === 0) return []

		return newTransactions.concat(
			await this.getPaginatedTransactions({
				last: newTransactions[newTransactions.length - 1].id,
				vezgo,
				filteredInstitutionIdsString,
			})
		)
	}

	static async getAllCryptoTransactions() {
		const cryptoProviders = await this.getCryptoProviders()
		const providerTypes = cryptoProviders.map(provider => provider.type)

		const institutions = await this.getInstitutions()
		const filteredInstitutionIdsString = institutions
			.filter(item => providerTypes.some(provider => provider === item.type || `vezgo_${provider}` === item.type))
			.map(item => item.id)
			.join(',')

		const transactions = await this.getPaginatedTransactions({ filteredInstitutionIdsString })

		return {
			transactions,
			vezgoTransactions: (
				await this.getPaginatedTransactions({
					vezgo: true,
					filteredInstitutionIdsString,
				})
			).filter(({ origin_id }) => transactions.some(trx => trx.origin_id === origin_id)),
		}
	}

	static async getNewCryptoTransactions(lastTransactionId?: string) {
		const cryptoProviders = await this.getCryptoProviders()
		const providerTypes = cryptoProviders.map(provider => provider.type)

		const institutions = await this.getInstitutions()
		const filteredInstitutionIdsString = institutions
			.filter(item => providerTypes.some(provider => provider === item.type || `vezgo_${provider}` === item.type))
			.map(item => item.id)
			.join(',')

		const transactions = await this.getPaginatedTransactions({ filteredInstitutionIdsString, last: lastTransactionId })

		const vezgoTransactions = await this.getPaginatedTransactions({
			last: lastTransactionId || '',
			filteredInstitutionIdsString, // at this point this param is ignored for vezgo=true
			vezgo: true,
		})

		return {
			transactions,
			vezgoTransactions: vezgoTransactions.filter(({ origin_id }) =>
				transactions.some(trx => trx.origin_id === origin_id)
			),
		}
	}

	static async getSubscription(): Promise<IWealthicaSubscriptionDto[]> {
		return await this.addon?.request({
			method: 'GET',
			endpoint: 'subscriptions',
		})
	}
}
